/**
 * Side effects of a background catalog refresh: live table updates while it runs and a
 * once-per-run toast + cache drop when it ends.
 *
 * Mounted by the BANNER ONLY (always mounted in MainLayout) so it can never double-fire
 * with the panel. The terminal branch keys off `lastOutcome.jobId` matching the job we
 * last saw running — never off `isRunning` flipping false, which a single failed or
 * mid-claim poll can do while the sweep is very much alive. A watched run that disappears
 * WITHOUT an outcome (its replica lost the lease or was killed) is reported too, after a
 * few consecutive polls, so the page never just goes quiet on the operator.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { smswiserCatalogKeys } from '@/hooks/smswiser/catalog/smswiserCatalogKeys';
import {
  invalidateAfterCatalogRefresh,
  type CatalogRefreshJobState,
} from '@/hooks/smswiser/catalog/refresh-job';
import { useToast } from '@/providers/ToastProvider';
import { outcomeSummaryText, outcomeTone } from './outcome-summary';
import { forgetWatchedJob, readWatchedJob, rememberWatchedJob } from './watched-job';

/** Polls with no run and no outcome before we call a watched run gone (3 x 3s ≈ 9s). */
const VANISHED_POLLS_BEFORE_WARNING = 3;

export function useCatalogRefreshTerminalEffect(
  state?: CatalogRefreshJobState,
  /**
   * The query's dataUpdatedAt. Two identical status responses share one object (react-query
   * structural sharing), so state identity alone cannot count polls — this timestamp is what
   * makes "the run has been missing for N polls" observable at all.
   */
  updatedAt = 0,
): void {
  const qc = useQueryClient();
  const toast = useToast();
  const notifiedJobIdRef = useRef<string | null>(null);
  const seenRef = useRef<{ jobId: string | null; done: number }>({ jobId: null, done: 0 });
  const vanishedPollsRef = useRef(0);

  useEffect(() => {
    if (state?.isRunning && state.jobId) rememberWatchedJob(state.jobId);
  }, [state?.isRunning, state?.jobId]);

  // Countries land one by one: refresh the table so "Last fetched" fills in live.
  useEffect(() => {
    const jobId = state?.jobId ?? null;
    const done = state?.progress.done ?? 0;
    const seen = seenRef.current;
    seenRef.current = { jobId, done };
    if (seen.jobId !== jobId || done <= seen.done) return;
    qc.invalidateQueries({ queryKey: smswiserCatalogKeys.countriesWithStatus() });
  }, [state, qc]);

  useEffect(() => {
    if (!state) return;
    const outcome = state.lastOutcome;
    const watched = readWatchedJob();
    if (!watched || notifiedJobIdRef.current === watched) return;

    if (outcome?.jobId === watched) {
      notifiedJobIdRef.current = watched;
      vanishedPollsRef.current = 0;
      forgetWatchedJob();
      invalidateAfterCatalogRefresh(qc);
      const description = outcomeSummaryText(outcome, state.progress);
      const tone = outcomeTone(outcome, outcome.failed ?? state.progress.failed);
      if (tone === 'success') toast.success('Catalog refresh complete', description);
      else if (tone === 'warning') toast.warning('Catalog refresh finished', description);
      else toast.error('Catalog refresh failed', description);
      return;
    }

    // The run we were watching is gone and published no outcome. A worker that loses its
    // Redis lease stops silently by design (it must not clobber a newer run's record), so
    // without this the page would just go blank — indistinguishable from never clicking.
    // Only after several consecutive polls, so a one-poll blip stays invisible.
    if (state.isRunning) {
      vanishedPollsRef.current = 0;
      return;
    }
    vanishedPollsRef.current += 1;
    if (vanishedPollsRef.current < VANISHED_POLLS_BEFORE_WARNING) return;
    notifiedJobIdRef.current = watched;
    vanishedPollsRef.current = 0;
    forgetWatchedJob();
    invalidateAfterCatalogRefresh(qc);
    toast.warning(
      'Catalog refresh stopped',
      'The run ended without reporting a result — the server was probably restarted. ' +
        'Countries already refreshed are saved; start it again to finish the rest.',
    );
  }, [state, updatedAt, qc, toast]);
}
